// Detects errors caused by missing/stale JS or CSS chunks after a deploy.
// The Swarm rolling update can leave a window where the served HTML references
// chunk hashes that the load-balanced replica doesn't have on disk — the
// resulting 404 surfaces as one of several error shapes across browsers and
// bundlers (webpack, Turbopack, Vite, native dynamic import).
const CHUNK_ERROR_PATTERNS: RegExp[] = [
  /Loading chunk [\w-]+ failed/i,
  /Loading CSS chunk [\w-]+ failed/i,
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Unable to preload CSS/i,
];

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  if (typeof error === "object" && error !== null) {
    const err = error as { name?: unknown; message?: unknown };
    if (typeof err.name === "string" && err.name === "ChunkLoadError") {
      return true;
    }
    if (typeof err.message === "string") {
      return CHUNK_ERROR_PATTERNS.some((p) => p.test(err.message as string));
    }
  }

  if (typeof error === "string") {
    return CHUNK_ERROR_PATTERNS.some((p) => p.test(error));
  }

  return false;
}

// Triggers a hard reload, with a guard against reload loops if the server
// itself is broken (would otherwise spin the user's browser forever).
const SESSION_KEY = "chunk-error-reload-attempts";
const MAX_ATTEMPTS = 2;
const RESET_AFTER_MS = 60_000;

export function reloadForChunkError(): void {
  if (typeof window === "undefined") return;

  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    const now = Date.now();
    const state = raw ? (JSON.parse(raw) as { count: number; first: number }) : null;

    if (state && now - state.first < RESET_AFTER_MS && state.count >= MAX_ATTEMPTS) {
      // Too many reloads in a short window — stop, let the visible error UI
      // show so the user (and we) can see something is really wrong.
      return;
    }

    const next =
      state && now - state.first < RESET_AFTER_MS
        ? { count: state.count + 1, first: state.first }
        : { count: 1, first: now };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
  } catch {
    // sessionStorage can throw in private mode / restricted contexts — fall
    // through and reload anyway.
  }

  window.location.reload();
}
